import { Lightbulb } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["suggestions"]) => void;
}

export function SuggestionsTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.suggestions;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  return (
    <Section
      icon={Lightbulb}
      title="Suggestions (/suggestion)"
      description="Users submit ideas with /suggestion. The bot adds ✅/❌ reactions and Approve / Reject buttons for staff."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          Members submit ideas with the slash command below. The bot posts an embed in your suggestions channel and adds
          reactions; staff with the configured roles (or admins) use the buttons on that message to approve or reject.
        </p>
      </div>

      <Card className="p-4 bg-muted/30">
        <h4 className="font-semibold mb-2 text-sm">Slash commands</h4>
        <div className="text-sm">
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
            <code className="text-primary font-mono text-xs shrink-0">/suggestion</code>
            <span className="text-xs text-muted-foreground">Submit a suggestion (text is shown in the embed).</span>
          </div>
        </div>
      </Card>

      <Field
        label="Suggestions channel"
        hint="Where the bot posts new suggestion embeds."
      >
        <ChannelPicker
          channels={channels}
          value={cfg.channel_id}
          onChange={(v) => update("channel_id", v === "__none__" ? "" : v)}
        />
      </Field>

      <Field
        label="Roles for approve / reject"
        hint="Only these roles can use the Approve / Reject buttons (admins are always included)."
      >
        <MultiSelect
          options={roles}
          value={cfg.approve_role_ids}
          onChange={(v) => update("approve_role_ids", v)}
          prefix="@"
          placeholder="+ add role"
        />
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">Create thread</div>
            <div className="text-xs text-muted-foreground">
              Auto discussion thread under each suggestion.
            </div>
          </div>
          <Switch
            checked={cfg.use_threads}
            onCheckedChange={(v) => update("use_threads", v)}
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">Allow anonymous</div>
            <div className="text-xs text-muted-foreground">
              When on, users can pass anonymous=True on /suggestion.
            </div>
          </div>
          <Switch
            checked={cfg.allow_anonymous}
            onCheckedChange={(v) => update("allow_anonymous", v)}
          />
        </div>
      </div>
    </Section>
  );
}
